import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { AuthenticationService } from '@app-auth/services/authentication.service';
import { LoginPageUtilService } from '@app-auth/services/login-page-util.service';
import { SUPPORTED_LANGUAGES } from '@app-core/constants/constants';
import { CognitoRegionType } from '@app-core/models/core.model';
import { CookieService } from '@app-core/services/cookie/cookie.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { FooterDirective } from '@app-shared/directives/footer/footer.directive';
import { resolveAsset } from '@app-shared/services/asset-resolver/asset-resolver.service';
import { getClientConfig } from '@config/client-config';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
})
export class LandingPageComponent implements OnInit, OnDestroy {
  @ViewChild(FooterDirective, { static: true }) footerHost: FooterDirective;

  public clientConfig = getClientConfig().configuration;
  public env = getClientConfig().environments;
  public copyrightyear = new Date().getFullYear();
  public regions: CognitoRegionType[] = [];
  public regionControl = new FormControl(
    { value: '', disabled: !!(this.clientConfig?.showLandingPage && this.clientConfig?.landingPageDetails?.disableRegionSelection) },
    Validators.required
  );
  public showRegionSelection;
  private destroy$ = new Subject<void>();

  public supportedLanguages = SUPPORTED_LANGUAGES;
  public languageControl = new FormControl('');
  public footerConfig = this.clientConfig?.showLandingPage ? this.clientConfig?.landingPageDetails?.footer ?? null : null;

  constructor(
    private authService: AuthenticationService,
    private loginUtilService: LoginPageUtilService,
    private snackBarService: SnackBarService,
    private cookieService: CookieService,
    public translate: TranslateService
  ) {
    this.showRegionSelection =
      Boolean(this.clientConfig?.showLandingPage ? this.clientConfig?.landingPageDetails?.defaultUserCountry : false) ||
      Object.keys(this.env.cognitoConfigs || {}).length > 1;
  }

  ngOnInit(): void {
    this.getRegionValue();
    //this.getLanguageValue();
    this.setEnglishLanguage();

    this.cookieService.deleteAllCookies();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSignIn() {
    const selectedRegion = this.regionControl.value;
    if (!selectedRegion && this.showRegionSelection) {
      this.snackBarService.failure(this.translate.instant('customLoginSelectRegionError'));
      return;
    }
    this.authService.authorizeUser();
  }

  get landingBackgroundStyle() {
    const bg = this.clientConfig?.showLandingPage ? this.clientConfig?.landingPageDetails?.backgroundImage : null;
    if (!bg) return null;

    return {
      'background-image': `url(${resolveAsset(bg)})`,
    };
  }

  public getRegionValue() {
    const { regions, previouslySelectedRegion } = this.loginUtilService.getRegionValue();
    this.regions = regions;
    if (previouslySelectedRegion) {
      this.regionControl.patchValue(previouslySelectedRegion);
    }

    this.regionControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((selectedRegion) => {
      this.loginUtilService.regionChanged(selectedRegion);
    });
  }

  public getLanguageValue() {
    const currentLanguage = this.loginUtilService.getCurrentLanguage();
    this.languageControl.patchValue(currentLanguage);

    this.languageControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((selectedLanguage) => {
      if (selectedLanguage) {
        this.loginUtilService.languageChanged(selectedLanguage);
      }
    });
  }

  public setEnglishLanguage() {
    !this.clientConfig.showCustomLoginPage && this.translate.use('en');
  }
}

// solera
// {
//   type: 'solera',
//   companyName: 'Solera Inc.',
//   termsLink: 'https://support.solera.com',
//   soleraPrivacyPolicyLink: 'https://support.solera.com',
//   privacyNoticeLink: 'https://support.solera.com',
//   soleraContactInfo: [
//     {
//       iso: 'US & CA',
//       email: 'https://support.solera.com',
//       phone: '+1 (800) 555-1234',
//     },
//     {
//       iso: 'UK',
//       email: 'https://support.solera.com',
//       phone: '+1 (800) 555-1234',
//     },
//     {
//       iso: 'MX',
//       email: 'https://support.solera.com',
//       phone: '+1 (800) 555-1234',
//     },
//   ],
// }

// satrack

// {
//   type: 'satrack',
//   companyName: 'SATRACK',
//   addresses:[
//     'Av. Palmas, Ed. Prisma Of. 9808 Medellín - Colombia',
//     'Av. República del Salvador E9-24 y Suiza,\nEdificio Euro, Piso 2, Oficina 2C Quito',
//     '9100 S. Dadeland Blvd. Suite 1500 Miami, FL 33156'
//   ],
//   satrackContactInfo: [
//     {
//       country: 'Colombia',
//       contact: '320 889 9930',
//       contactTitle: 'Contacto',
//       supportEmail: 'soportelm@satrack.com',
//       supportEmailTitle: 'Correo de soporte',
//       privacyPolicy: 'https://support.solera.com',
//       privacyPolicyTitle: 'Políticas de uso de datos',
//       termsConditions: 'https://support.solera.com',
//       termsConditionsTitle: 'Términos y condiciones'
//     },
//     {
//       country: 'Ecuador',
//       contact: '0963 16 6655',
//       contactTitle: 'Contacto',
//       supportEmail: 'soportelm@satrack.com',
//       supportEmailTitle: 'Correo de soporte',
//       privacyPolicy: 'https://support.solera.com',
//       privacyPolicyTitle: 'Políticas de uso de datos',
//       termsConditions: 'https://support.solera.com',
//       termsConditionsTitle: 'Términos y condiciones'
//     },
//     {
//       country: 'United States',
//       contact: '(786) 980-2021',
//       contactTitle: 'Phone',
//       supportEmail: 'support_dashcam@satrack.com',
//       supportEmailTitle: 'Support email',
//       privacyPolicy: 'https://support.solera.com',
//       privacyPolicyTitle: 'Privacy policy',
//       termsConditions: 'https://support.solera.com',
//       termsConditionsTitle: 'Terms & Conditions'
//     },
//   ]
// };

// defualt

// {
//   type: 'default',
//   showPrivacyPolicy: true,
//   companyName: 'Safe Fleet, Inc.',
//   showFooterPrivacyText: false,
//   privacyPolicyLink: 'https://support.solera.com',
//   email: '',
//   phone: '',
//   supportUrl: 'https://www.safefleet.net/support/school-bus-transit-support/video-fleet-management/',
//   supportText: 'Support'
// }
